import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatFileSize } from '../lib/utils';

interface MediaFile {
  id: string;
  file: File;
  preview: string;
  type: 'image' | 'video';
  uploading: boolean;
  progress: number;
  error?: string;
  uploadedUrl?: string;
  mediaId?: string;
}

interface Segment {
  id: string;
  name: string;
  waterway: { id: string; name: string };
}

export default function CreatePostPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [media, setMedia] = useState<MediaFile[]>([]);
  const [segment, setSegment] = useState<Segment | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [segmentSearch, setSegmentSearch] = useState('');
  const [showSegmentPicker, setShowSegmentPicker] = useState(false);
  const [attachWeather, setAttachWeather] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Load segment from URL
  useEffect(() => {
    const segmentId = searchParams.get('segment');
    if (segmentId) {
      api.waterways.getSegment(segmentId).then(setSegment).catch(console.error);
    }
  }, [searchParams]);
  
  // Search segments
  useEffect(() => {
    if (!segmentSearch.trim()) {
      setSegments([]);
      return;
    }
    
    const search = async () => {
      try {
        const res = await api.waterways.searchSegments({ query: segmentSearch });
        setSegments(res);
      } catch (err) {
        console.error('Failed to search segments:', err);
      }
    };
    
    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [segmentSearch]);
  
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Check total media count
    if (media.length + files.length > 4) {
      setError('Maximum 4 media files allowed');
      return;
    }
    
    // Process files
    const newMedia: MediaFile[] = [];
    
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        setError('Only images and videos are allowed');
        continue;
      }
      
      // Validate file size (100MB max)
      if (file.size > 100 * 1024 * 1024) {
        setError(`${file.name} is too large. Maximum size is 100MB`);
        continue;
      }
      
      // Create preview
      const preview = URL.createObjectURL(file);
      
      newMedia.push({
        id: Math.random().toString(36).slice(2),
        file,
        preview,
        type: isImage ? 'image' : 'video',
        uploading: false,
        progress: 0,
      });
    }
    
    setMedia((prev) => [...prev, ...newMedia]);
    
    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveMedia = (id: string) => {
    setMedia((prev) => {
      const item = prev.find((m) => m.id === id);
      if (item) {
        URL.revokeObjectURL(item.preview);
      }
      return prev.filter((m) => m.id !== id);
    });
  };
  
  const uploadMedia = async (item: MediaFile): Promise<string | null> => {
    setMedia((prev) =>
      prev.map((m) => (m.id === item.id ? { ...m, uploading: true, error: undefined } : m))
    );
    
    try {
      const res = await api.media.upload(item.file);
      
      setMedia((prev) =>
        prev.map((m) =>
          m.id === item.id ? { ...m, uploading: false, uploadedUrl: res.url, mediaId: res.id } : m
        )
      );
      
      return res.id || null;
    } catch (err: any) {
      setMedia((prev) =>
        prev.map((m) =>
          m.id === item.id
            ? { ...m, uploading: false, error: err.message || 'Upload failed' }
            : m
        )
      );
      return null;
    }
  };
  
  const handleSubmit = async () => {
    // Validate
    if (!content.trim() && media.length === 0) {
      setError('Please add some content or media');
      return;
    }
    
    if (media.length === 0) {
      setError('At least one image or video is required');
      return;
    }
    
    setError('');
    setSubmitting(true);
    
    try {
      // Upload all media
      const mediaIds: string[] = [];
      for (const item of media) {
        if (item.mediaId) {
          mediaIds.push(item.mediaId);
        } else {
          const id = await uploadMedia(item);
          if (id) {
            mediaIds.push(id);
          } else {
            throw new Error('Failed to upload media');
          }
        }
      }
      
      if (!segment) {
        throw new Error('Please select a location');
      }
      
      // Create post
      const post = await api.posts.create({
        content: content.trim(),
        mediaIds,
        segmentId: segment.id,
        attachWeather,
      });
      
      // Navigate to post
      navigate(`/post/${post.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };
  
  const canSubmit = (content.trim() || media.length > 0) && media.length > 0 && !submitting;
  
  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Create Post</h1>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="btn btn-primary"
        >
          {submitting ? 'Posting...' : 'Post'}
        </button>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">
          {error}
        </div>
      )}
      
      <div className="card">
        {/* Location picker */}
        <div className="p-4 border-b border-slate-100 dark:border-slate-700">
          {segment ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LocationIcon className="w-5 h-5 text-primary-500" />
                <span className="font-medium text-slate-900 dark:text-white">{segment.name}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500 dark:text-slate-400">{segment.waterway.name}</span>
              </div>
              <button
                onClick={() => setSegment(null)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <button
                onClick={() => setShowSegmentPicker(!showSegmentPicker)}
                className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-primary-500 transition-colors"
              >
                <LocationIcon className="w-5 h-5" />
                <span>Add location</span>
              </button>
              
              {showSegmentPicker && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-10">
                  <div className="p-3">
                    <input
                      type="text"
                      value={segmentSearch}
                      onChange={(e) => setSegmentSearch(e.target.value)}
                      placeholder="Search canal segments..."
                      className="input"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {segments.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => {
                          setSegment(s);
                          setShowSegmentPicker(false);
                          setSegmentSearch('');
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <p className="font-medium text-slate-900 dark:text-white">{s.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{s.waterway.name}</p>
                      </button>
                    ))}
                    {segmentSearch && segments.length === 0 && (
                      <p className="px-4 py-3 text-slate-500 dark:text-slate-400 text-sm">
                        No segments found
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's happening on the water?"
            className="w-full min-h-[120px] bg-transparent border-0 resize-none focus:ring-0 text-slate-900 dark:text-white placeholder-slate-400"
            maxLength={500}
          />
        </div>
        
        {/* Media preview */}
        {media.length > 0 && (
          <div className="px-4 pb-4">
            <div className={`grid gap-2 ${media.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {media.map((item) => (
                <div key={item.id} className="relative rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
                  {item.type === 'image' ? (
                    <img
                      src={item.preview}
                      alt=""
                      className="w-full aspect-video object-cover"
                    />
                  ) : (
                    <video
                      src={item.preview}
                      className="w-full aspect-video object-cover"
                      controls
                    />
                  )}
                  
                  {/* Upload progress */}
                  {item.uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-2" />
                        <span className="text-sm">{item.progress}%</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Error */}
                  {item.error && (
                    <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
                      <p className="text-white text-sm px-4 text-center">{item.error}</p>
                    </div>
                  )}
                  
                  {/* Remove button */}
                  <button
                    onClick={() => handleRemoveMedia(item.id)}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                  
                  {/* File info */}
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
                    {formatFileSize(item.file.size)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={media.length >= 4}
              className="p-2 text-slate-500 dark:text-slate-400 hover:text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50"
              title="Add media"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setAttachWeather(!attachWeather)}
              className={`p-2 rounded-lg transition-colors ${
                attachWeather
                  ? 'text-primary-500 bg-primary-50 dark:bg-primary-900/30'
                  : 'text-slate-500 dark:text-slate-400 hover:text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              title={attachWeather ? 'Weather attached' : 'Attach weather'}
            >
              <WeatherIcon className="w-5 h-5" />
            </button>
          </div>
          
          <span className={`text-sm ${content.length > 450 ? 'text-orange-500' : 'text-slate-400'}`}>
            {content.length}/500
          </span>
        </div>
      </div>
      
      {/* Guidelines */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <h3 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Posting Guidelines</h3>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
          <li>• At least one image or video is required</li>
          <li>• Be respectful to other canal users</li>
          <li>• No spam or promotional content</li>
          <li>• Don't share exact mooring locations for safety</li>
        </ul>
      </div>
    </div>
  );
}

// Icons
function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ImageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function WeatherIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>
  );
}
